import { useState, useEffect, useCallback, useRef } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import { Modal } from '../components/Modal';
import { VerInstructivo } from '../components/VerInstructivo';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MLItem {
  id: string;
  title: string;
  thumbnail: string | null;
  price: number;
  available_quantity: number;
  permalink?: string | null;
}

interface MLQuestion {
  id: number;
  text: string;
  status: 'UNANSWERED' | 'ANSWERED' | 'CLOSED_UNANSWERED';
  date_created: string;
  item_id: string;
  seller_id: number;
  from: { id: number; answered_questions: number; nickname?: string | null };
  answer?: { text: string; date_created: string; status: string; answered_by?: string | null } | null;
  item: MLItem | null;
}

interface PreguntasResponse {
  questions: MLQuestion[];
  total: number;
  paging: { total: number; limit: number; offset: number } | null;
  /** Switch "Responder preguntas en Mercado Libre" (encendido por defecto). */
  responderPreguntasHabilitado?: boolean;
}

/** Límite de MercadoLibre para el texto de una respuesta (el backend valida lo mismo). */
const MAX_CARACTERES_RESPUESTA = 2000;

interface RespuestaRapida {
  id: number;
  etiqueta: string;
  texto: string;
  color: string;
  orden: number;
}

// ---------------------------------------------------------------------------
// Paleta de colores disponibles
// ---------------------------------------------------------------------------

const COLOR_OPTIONS = [
  // Cálidos
  { label: 'Rojo',       value: '#ef4444' },
  { label: 'Coral',      value: '#f87171' },
  { label: 'Naranja',    value: '#f97316' },
  { label: 'Ámbar',      value: '#f59e0b' },
  { label: 'Amarillo',   value: '#eab308' },
  { label: 'Lima',       value: '#84cc16' },
  { label: 'Verde',      value: '#22c55e' },
  { label: 'Esmeralda',  value: '#10b981' },
  // Fríos
  { label: 'Teal',       value: '#14b8a6' },
  { label: 'Cyan',       value: '#06b6d4' },
  { label: 'Celeste',    value: '#38bdf8' },
  { label: 'Azul',       value: '#3b82f6' },
  { label: 'Índigo',     value: '#6366f1' },
  { label: 'Violeta',    value: '#8b5cf6' },
  { label: 'Púrpura',    value: '#a855f7' },
  { label: 'Rosa',       value: '#ec4899' },
  // Neutros
  { label: 'Fucsia',     value: '#f43f5e' },
  { label: 'Gris azul',  value: '#64748b' },
  { label: 'Gris medio', value: '#475569' },
  { label: 'Gris oscuro',value: '#374151' },
  { label: 'Casi negro', value: '#1e293b' },
];

// ---------------------------------------------------------------------------
// Status tabs
// ---------------------------------------------------------------------------

const TABS: { value: 'UNANSWERED' | 'ANSWERED' | 'CLOSED_UNANSWERED'; label: string }[] = [
  { value: 'UNANSWERED', label: 'Pendientes' },
  { value: 'ANSWERED', label: 'Historial' },
  { value: 'CLOSED_UNANSWERED', label: 'Sin responder' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatResponseTime(questionDate: string, answerDate: string): string {
  const diff = new Date(answerDate).getTime() - new Date(questionDate).getTime();
  if (diff <= 0) return '< 1 min';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const hrs = hours % 24;
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
}

function formatPrice(n: number) {
  return '$' + n.toLocaleString('es-AR');
}

// ---------------------------------------------------------------------------
// Modal — Modificar respuestas rápidas
// ---------------------------------------------------------------------------

function ModalRespuestas({
  respuestas,
  onClose,
  onSaved,
}: {
  respuestas: RespuestaRapida[];
  onClose: () => void;
  onSaved: (lista: RespuestaRapida[]) => void;
}) {
  const { addToast } = useToast();
  const [items, setItems] = useState<RespuestaRapida[]>(respuestas);
  const [newLabel, setNewLabel] = useState('');
  const [newText, setNewText] = useState('');
  const [newColor, setNewColor] = useState('#ef4444');
  const [saving, setSaving] = useState(false);
  // Agregar y borrar se guardan al instante: lo único que se puede perder es la respuesta nueva a medio escribir.
  const cierre = useCierreConfirmado(useHayCambios({ newLabel, newText }, true, { newLabel: '', newText: '' }), onClose);

  const handleAdd = async () => {
    if (!newLabel.trim() || !newText.trim()) return;
    setSaving(true);
    try {
      const created = await api.post<RespuestaRapida>('/api/v3/ml/questions/respuestas-rapidas', {
        etiqueta: newLabel.trim().toUpperCase(),
        texto: newText.trim(),
        color: newColor,
        orden: items.length,
      });
      const next = [...items, created];
      setItems(next);
      onSaved(next);
      setNewLabel('');
      setNewText('');
      setNewColor('#ef4444');
      addToast('Respuesta rápida agregada', 'success');
    } catch (err) {
      addToast(extractErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/v3/ml/questions/respuestas-rapidas/${id}`);
      const next = items.filter((r) => r.id !== id);
      setItems(next);
      onSaved(next);
      addToast('Eliminada', 'success');
    } catch (err) {
      addToast(extractErrorMessage(err), 'error');
    }
  };

  return (
    <Modal open cierre={cierre} title="Respuestas rápidas" size="md">
      {/* Las secciones tienen su propio relleno: se anula el del cuerpo de la ventana. */}
      <div className="-m-6">

        {/* Nueva respuesta */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex gap-2 mb-2">
            {/* Color picker */}
            <div className="shrink-0">
              <p className="text-[10px] text-slate-400 mb-1 font-medium uppercase tracking-wide">Color</p>
              <div className="flex gap-1.5 flex-wrap w-[90px]">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setNewColor(c.value)}
                    title={c.label}
                    className="w-5 h-5 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: c.value,
                      borderColor: newColor === c.value ? '#1e293b' : 'transparent',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Etiqueta */}
            <div className="flex-1">
              <p className="text-[10px] text-slate-400 mb-1 font-medium uppercase tracking-wide">Etiqueta</p>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="text-white text-xs font-bold px-3 py-1.5 rounded-lg min-w-[80px] text-center"
                  style={{ backgroundColor: newColor }}
                >
                  {newLabel || 'ETIQUETA'}
                </div>
                <input
                  type="text"
                  placeholder="Ej: GRABADO"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value.toUpperCase())}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
              <p className="text-[10px] text-slate-400 mb-1 font-medium uppercase tracking-wide">Respuesta</p>
              <div className="flex gap-2">
                <textarea
                  placeholder="Ingresá la respuesta rápida"
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  rows={2}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                <button
                  onClick={handleAdd}
                  disabled={!newLabel.trim() || !newText.trim() || saving}
                  className="self-end px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1 px-6 py-3 space-y-2">
          {items.map((r) => (
            <div key={r.id} className="flex items-start gap-3 py-2 border-b border-slate-50">
              <div
                className="shrink-0 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg min-w-[80px] text-center mt-0.5"
                style={{ backgroundColor: r.color }}
              >
                {r.etiqueta}
              </div>
              <p className="flex-1 text-sm text-slate-600 leading-relaxed">{r.texto}</p>
              <button
                onClick={() => handleDelete(r.id)}
                className="shrink-0 text-slate-300 hover:text-red-500 transition-colors mt-0.5"
                title="Borrar"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">Sin respuestas rápidas</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function PreguntasPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'UNANSWERED' | 'ANSWERED' | 'CLOSED_UNANSWERED'>('UNANSWERED');
  const [questions, setQuestions] = useState<MLQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [respuestas, setRespuestas] = useState<RespuestaRapida[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [activeDraft, setActiveDraft] = useState<Record<number, string>>({});
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [responderHabilitado, setResponderHabilitado] = useState(true);
  const LIMIT = 20;

  const load = useCallback(async (status: string, off: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<PreguntasResponse>(
        `/api/v3/ml/questions?status=${status}&limit=${LIMIT}&offset=${off}`,
      );
      setQuestions(data.questions);
      setTotal(data.total);
      setResponderHabilitado(data.responderPreguntasHabilitado !== false);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRespuestas = useCallback(async () => {
    try {
      const data = await api.get<RespuestaRapida[]>('/api/v3/ml/questions/respuestas-rapidas');
      setRespuestas(data);
    } catch {
      // silencioso — panel lateral se queda vacío
    }
  }, []);

  useEffect(() => {
    setOffset(0);
    load(tab, 0);
  }, [tab, load]);

  useEffect(() => {
    loadRespuestas();
  }, [loadRespuestas]);

  const handleAnswered = (id: number, text: string) => {
    if (tab === 'UNANSWERED') {
      setQuestions((prev) => prev.filter((q) => q.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } else {
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === id
            ? { ...q, status: 'ANSWERED', answer: { text, date_created: new Date().toISOString(), status: 'ACTIVE', answered_by: user?.username ?? null } }
            : q,
        ),
      );
    }
  };

  const setDraftForQuestion = (questionId: number, text: string) => {
    setActiveDraft((prev) => ({ ...prev, [questionId]: text }));
  };

  const unansweredCount = tab === 'UNANSWERED' ? total : undefined;

  return (
    <div className="flex gap-6 h-full">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 inline">Preguntas ML</h1><VerInstructivo seccion="preguntas-ml" />
            <p className="text-sm text-slate-500 mt-0.5">Preguntas de compradores en MercadoLibre</p>
          </div>
          <button
            onClick={() => load(tab, offset)}
            disabled={loading}
            className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              {t.value === 'UNANSWERED' && unansweredCount !== undefined && unansweredCount > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {unansweredCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && questions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-400">Cargando preguntas...</p>
          </div>
        )}

        {!responderHabilitado && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-900">
            Responder preguntas en Mercado Libre está deshabilitado. Las preguntas se pueden ver, pero no contestar desde el ERP.
            Un administrador lo habilita en Configuración → Mercado Libre y catálogo, «Responder preguntas en Mercado Libre».
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-700">Error al cargar preguntas</p>
              <p className="text-xs text-red-500 mt-0.5">{error}</p>
              <button onClick={() => load(tab, offset)} className="mt-2 text-xs text-red-600 underline hover:no-underline">
                Reintentar
              </button>
            </div>
          </div>
        )}

        {!loading && !error && questions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                {tab === 'UNANSWERED' ? '¡Todo al día!' : 'Sin preguntas'}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {tab === 'UNANSWERED' ? 'No hay preguntas pendientes.' : 'No hay preguntas en esta categoría.'}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {questions.map((q) => (
            <QuestionCardWithSidebar
              key={q.id}
              q={q}
              onAnswered={handleAnswered}
              draft={activeDraft[q.id] ?? ''}
              onDraftChange={(text) => setDraftForQuestion(q.id, text)}
              onFocus={() => setActiveQuestionId(q.id)}
              responderHabilitado={responderHabilitado}
            />
          ))}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <span className="text-sm text-slate-500">
              {offset + 1}–{Math.min(offset + LIMIT, total)} de {total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(tab, o); }}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                disabled={offset + LIMIT >= total}
                onClick={() => { const o = offset + LIMIT; setOffset(o); load(tab, o); }}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Panel lateral — respuestas rápidas */}
      <div className="w-56 shrink-0">
        <div className="sticky top-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Respuestas rápidas</p>
              <input
                type="text"
                placeholder="Buscar..."
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent placeholder:text-slate-300"
              />
            </div>
            <div className="p-3 flex flex-wrap gap-1.5 max-h-[calc(100vh-280px)] overflow-y-auto">
              {respuestas
                .filter((r) =>
                  !sidebarSearch.trim() ||
                  r.etiqueta.toLowerCase().includes(sidebarSearch.toLowerCase()) ||
                  r.texto.toLowerCase().includes(sidebarSearch.toLowerCase())
                )
                .map((r) => (
                  <button
                    key={r.id}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-white transition-opacity hover:opacity-80"
                    style={{ backgroundColor: r.color }}
                    onClick={() => {
                      if (activeQuestionId !== null) {
                        setDraftForQuestion(activeQuestionId, r.texto);
                      } else if (navigator.clipboard) {
                        navigator.clipboard.writeText(r.texto);
                      }
                    }}
                    title={r.texto}
                  >
                    {r.etiqueta}
                  </button>
                ))}
              {respuestas.length === 0 && (
                <p className="text-xs text-slate-400 w-full text-center py-2">Sin respuestas</p>
              )}
              {respuestas.length > 0 && sidebarSearch.trim() &&
                respuestas.filter((r) =>
                  r.etiqueta.toLowerCase().includes(sidebarSearch.toLowerCase()) ||
                  r.texto.toLowerCase().includes(sidebarSearch.toLowerCase())
                ).length === 0 && (
                  <p className="text-xs text-slate-400 w-full text-center py-2">Sin coincidencias</p>
                )}
            </div>
            {activeQuestionId !== null && (
              <div className="px-3 pt-0 pb-2">
                <p className="text-[10px] text-brand-500 text-center">
                  Clic aplica a pregunta activa
                </p>
              </div>
            )}
            <div className="px-3 pb-3">
              <button
                onClick={() => setShowModal(true)}
                className="w-full text-xs text-slate-500 hover:text-brand-600 font-medium flex items-center justify-center gap-1.5 py-2 border border-slate-200 rounded-lg hover:border-brand-300 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Modificar respuestas rápidas
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <ModalRespuestas
          respuestas={respuestas}
          onClose={() => setShowModal(false)}
          onSaved={(lista) => setRespuestas(lista)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuestionCardWithSidebar — versión que recibe respuestas rápidas como prop
// ---------------------------------------------------------------------------

function QuestionCardWithSidebar({
  q,
  onAnswered,
  draft,
  onDraftChange,
  onFocus,
  responderHabilitado,
}: {
  q: MLQuestion;
  onAnswered: (id: number, text: string) => void;
  draft: string;
  onDraftChange: (text: string) => void;
  onFocus: () => void;
  responderHabilitado: boolean;
}) {
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { addToast } = useToast();

  const excedido = draft.trim().length > MAX_CARACTERES_RESPUESTA;
  const puedeEnviar = responderHabilitado && !!draft.trim() && !excedido && !sending;

  const handleSend = async () => {
    const text = draft.trim();
    if (!puedeEnviar) return;
    setSending(true);
    try {
      await api.post(`/api/v3/ml/questions/${q.id}/answer`, { text });
      onAnswered(q.id, text);
      onDraftChange('');
      addToast('Respuesta enviada', 'success');
    } catch (err) {
      addToast(extractErrorMessage(err), 'error');
    } finally {
      setSending(false);
    }
  };

  const isAnswered = q.status === 'ANSWERED';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Item header */}
      <div className="flex items-start gap-3 px-5 pt-4 pb-3 border-b border-slate-100">
        {q.item?.thumbnail ? (
          <img src={q.item.thumbnail} alt={q.item.title} className="w-12 h-12 rounded-lg object-contain border border-slate-100 shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          {q.item?.permalink ? (
            <a href={q.item.permalink} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-800 hover:text-brand-600 hover:underline truncate block">
              {q.item.title}
            </a>
          ) : (
            <p className="text-sm font-medium text-slate-800 truncate">{q.item?.title ?? q.item_id}</p>
          )}
          <div className="flex items-center gap-3 mt-0.5">
            {q.item?.price && <span className="text-sm font-semibold text-emerald-600">{formatPrice(q.item.price)}</span>}
            <span className="text-xs text-slate-400">#{q.item_id}</span>
            {q.item?.available_quantity !== undefined && <span className="text-xs text-slate-400">{q.item.available_quantity} disp.</span>}
          </div>
        </div>
        {isAnswered && (
          <span className="shrink-0 text-xs bg-emerald-50 text-emerald-600 font-medium px-2.5 py-0.5 rounded-full">Respondida</span>
        )}
      </div>

      {/* Question body */}
      <div className="px-5 py-4">
        <div className="flex items-center gap-1.5 mb-2">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-xs text-slate-400">
            <span className="font-medium text-slate-600">{q.from.nickname ?? `#${q.from.id}`}</span>
            {' '}({q.from.answered_questions} preguntas) · <span title={new Date(q.date_created).toLocaleString('es-AR')}>{timeAgo(q.date_created)}</span>
          </span>
        </div>

        <div className="flex items-start gap-2 mb-4">
          <span className="shrink-0 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center mt-0.5">
            <svg className="w-3 h-3 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <p className="text-sm text-slate-800 leading-relaxed">{q.text}</p>
        </div>

        {isAnswered && q.answer && (
          <div className="ml-7 pl-3 border-l-2 border-emerald-200 mb-4">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wide">
                {q.answer.answered_by ? `Respondida por ${q.answer.answered_by}` : 'Tu respuesta'}
              </span>
              <span className="text-xs text-slate-400">
                {formatDateTime(q.answer.date_created)}
              </span>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">
                {formatResponseTime(q.date_created, q.answer.date_created)} · Tiempo de respuesta
              </span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{q.answer.text}</p>
          </div>
        )}

        {!isAnswered && (
          <div className="ml-7">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              rows={3}
              placeholder="Ingresá tu respuesta..."
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent placeholder:text-slate-300 text-slate-700"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
              onFocus={onFocus}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-400">
                {excedido ? (
                  <span className="text-red-600">{draft.trim().length} de {MAX_CARACTERES_RESPUESTA} caracteres: Mercado Libre no admite respuestas más largas · </span>
                ) : (
                  draft.length > 0 && `${draft.length} caracteres · `
                )}
                Ctrl+Enter para enviar
              </span>
              <button
                onClick={handleSend}
                disabled={!puedeEnviar}
                title={responderHabilitado ? undefined : 'Responder preguntas en Mercado Libre está deshabilitado'}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
                Enviar respuesta
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
